import React, { useState, useEffect, useRef } from 'react';
import BaseModal, { ModalFooter } from '../base/BaseModal';
import { useAuth } from '../../contexts/AuthContext';
import { validateCreateReport } from '../../validator/reportValidator';
import { showToast } from '../../utils/toastNotification';
import { UploadCloud, X, File, Image as ImageIcon, Video, ChevronDown } from 'lucide-react';
import CustomSelect from '../base/CustomSelect';
import { VIDEO_API } from '../../apis/video';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const KHU_PHO_LIST = Array.from({ length: 45 }, (_, i) => `Khu phố ${i + 1}`);

const ReportCreateModal = ({ isOpen, onClose, onSubmit, isLoading, reportAreas, extent }) => {
    const { auth } = useAuth();
    const user = auth?.user;
    
    const [formData, setFormData] = useState({
        tieuDe: '',
        moTa: '',
        idLinhVucPhanAnh: '',
        mucDo: '',
        viTri: '',
        khuPho: '',
        tenNguoiPhanAnh: user?.username || '',
        soDienThoaiNguoiPhanAnh: '',
        files: []
    });

    const [errors, setErrors] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [uploadingVideo, setUploadingVideo] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showKhuPhoDropdown, setShowKhuPhoDropdown] = useState(false);
    const fileInputRef = useRef(null);
    const khuPhoRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (khuPhoRef.current && !khuPhoRef.current.contains(event.target)) {
                setShowKhuPhoDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (isOpen) {
            resetForm();
        }
    }, [isOpen]);

    const resetForm = () => {
        setFormData({
            tieuDe: '',
            moTa: '',
            idLinhVucPhanAnh: '',
            mucDo: '',
            viTri: '',
            khuPho: '',
            tenNguoiPhanAnh: user?.username || '',
            soDienThoaiNguoiPhanAnh: '',
            files: []
        });
        setErrors({});
        setShowKhuPhoDropdown(false);
        setUploadingVideo(false);
        setUploadProgress(0);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'soDienThoaiNguoiPhanAnh') {
            if (value !== '' && !/^\d*$/.test(value)) {
                return;
            }
            if (value.length > 10) {
                return;
            }
        }
        setFormData(prev => ({ ...prev, [name]: value }));
        
        if (errors[name]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        const trimmed = (value || '').trim();
        if (name === 'tenNguoiPhanAnh') {
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    tenNguoiPhanAnh: 'Họ và tên người phản ánh là bắt buộc'
                }));
            } else if (trimmed.length < 2) {
                setErrors(prev => ({
                    ...prev,
                    tenNguoiPhanAnh: 'Họ và tên phải có ít nhất 2 ký tự'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.tenNguoiPhanAnh;
                    return newErrors;
                });
            }
        } else if (name === 'soDienThoaiNguoiPhanAnh') {
            if (!trimmed) {
                setErrors(prev => ({
                    ...prev,
                    soDienThoaiNguoiPhanAnh: 'Số điện thoại là bắt buộc'
                }));
            } else if (!/^(?:0|\+84|84)(3|5|7|8|9)\d{8}$/.test(trimmed)) {
                setErrors(prev => ({
                    ...prev,
                    soDienThoaiNguoiPhanAnh: 'Số điện thoại không hợp lệ (hỗ trợ 10 chữ số đầu 03, 05, 07, 08, 09 hoặc định dạng +84)'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.soDienThoaiNguoiPhanAnh;
                    return newErrors;
                });
            }
        }
    };

    const processFiles = (newFiles) => {
        const validTypes = ['image/jpeg', 'image/png', 'video/mp4'];
        const currentCount = formData.files.length;
        
        const filteredFiles = newFiles.filter(file => {
            if (!validTypes.includes(file.type)) {
                showToast.warning(`File ${file.name} không đúng định dạng (chỉ hỗ trợ JPEG, PNG, MP4)`);
                return false;
            }
            if (file.size > 50 * 1024 * 1024 && file.type === 'video/mp4') {
                showToast.warning(`Video ${file.name} vượt quá dung lượng cho phép (tối đa 50MB)`);
                return false;
            }
            if (file.size > 5 * 1024 * 1024 && file.type !== 'video/mp4') {
                showToast.warning(`Ảnh ${file.name} vượt quá dung lượng cho phép (tối đa 5MB)`);
                return false;
            }
            return true;
        });

        if (currentCount + filteredFiles.length > 5) {
            showToast.warning('Chỉ được tải lên tối đa 5 file đính kèm');
            return;
        }

        if (filteredFiles.length > 0) {
            setFormData(prev => ({
                ...prev,
                files: [...prev.files, ...filteredFiles]
            }));

            if (errors.files) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors.files;
                    return newErrors;
                });
            }
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            processFiles(Array.from(e.target.files));
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            processFiles(Array.from(e.dataTransfer.files));
        }
    };

    const removeFile = (index) => {
        setFormData(prev => {
            const newFiles = [...prev.files];
            newFiles.splice(index, 1);
            return { ...prev, files: newFiles };
        });
    };

    const uploadVideos = async (videoFiles) => {
        const videoIds = [];
        let totalProcessedChunks = 0;
        const totalOverallChunks = videoFiles.reduce((acc, file) => acc + Math.ceil(file.size / CHUNK_SIZE), 0);

        for (const file of videoFiles) {
            const idVideo = crypto.randomUUID();
            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                // Cần truyền file.type vào slice để Blob giữ được định dạng mimetype (ví dụ: video/mp4), nếu không Backend sẽ nhận là application/octet-stream và từ chối.
                const chunk = file.slice(start, end, file.type);
                
                await VIDEO_API.uploadChunk(idVideo, chunk, i, totalChunks);
                
                totalProcessedChunks++;
                setUploadProgress(Math.round((totalProcessedChunks / totalOverallChunks) * 100));
            }
            videoIds.push(idVideo);
        }
        return videoIds;
    };

    const handleSubmit = async () => {
        const dataToValidate = {
            ...formData
        };

        const { isValid, errors: validationErrors } = await validateCreateReport(dataToValidate);

        if (!KHU_PHO_LIST.includes(formData.khuPho)) {
            validationErrors.khuPho = 'Vui lòng nhập số và chọn Khu phố từ danh sách.';
        }

        if (!isValid || Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showToast.error('Vui lòng kiểm tra lại các trường bắt buộc!');
            return;
        }

        setErrors({});
        
        try {
            setUploadingVideo(true);
            const imageFiles = formData.files.filter(f => f.type.startsWith('image/'));
            const videoFiles = formData.files.filter(f => f.type.startsWith('video/'));
            
            let idVideos = [];
            if (videoFiles.length > 0) {
                setUploadProgress(0);
                idVideos = await uploadVideos(videoFiles);
            }

            // Prepare final data to submit
            const rawPhone = formData.soDienThoaiNguoiPhanAnh ? formData.soDienThoaiNguoiPhanAnh.trim() : '';
            const normalizedPhone = rawPhone.startsWith('+84')
                ? `0${rawPhone.slice(3)}`
                : (rawPhone.startsWith('84') && rawPhone.length === 11)
                    ? `0${rawPhone.slice(2)}`
                    : rawPhone;

            const finalData = {
                tieuDe: formData.tieuDe,
                moTa: formData.moTa,
                idLinhVucPhanAnh: formData.idLinhVucPhanAnh,
                mucDo: formData.mucDo,
                viTri: formData.viTri,
                khuPho: formData.khuPho,
                tenNguoiPhanAnh: formData.tenNguoiPhanAnh,
                soDienThoaiNguoiPhanAnh: normalizedPhone,
                idVideo: idVideos, // This is an array, the modified API will append it correctly
                files: imageFiles // Pass images as files
            };

            await onSubmit(finalData);
        } catch (error) {
            showToast.error('Lỗi khi tải lên file: ' + (error.message || 'Thử lại sau'));
            console.error('Upload Error:', error);
        } finally {
            setUploadingVideo(false);
            setUploadProgress(0);
        }
    };

    return (
        <BaseModal
            isOpen={isOpen}
            onClose={onClose}
            title="Tạo phản ánh mới"
            size="3xl"
            footer={
                <ModalFooter
                    onCancel={onClose}
                    onSubmit={handleSubmit}
                    submitText="Tạo phản ánh"
                    submitLoading={isLoading || uploadingVideo}
                    submitDisabled={isLoading || uploadingVideo}
                />
            }
        >
            <div className="space-y-4">
                {uploadingVideo && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-sm font-medium text-blue-700">Đang tải lên Video...</span>
                            <span className="text-sm font-medium text-blue-700">{uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-2.5">
                            <div className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }}></div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Danh mục <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                            name="idLinhVucPhanAnh"
                            value={formData.idLinhVucPhanAnh}
                            onChange={handleInputChange}
                            placeholder="Chọn lĩnh vực"
                            hasError={Boolean(errors.idLinhVucPhanAnh)}
                            disabled={isLoading || uploadingVideo}
                            options={reportAreas ? reportAreas.map(area => ({ value: area.id, label: area.ten })) : []}
                        />
                        {errors.idLinhVucPhanAnh && (
                            <p className="mt-1 text-sm text-red-500">{errors.idLinhVucPhanAnh}</p>
                        )}
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Mức độ <span className="text-red-500">*</span>
                        </label>
                        <CustomSelect
                            name="mucDo"
                            value={formData.mucDo}
                            onChange={handleInputChange}
                            placeholder="Chọn mức độ"
                            hasError={Boolean(errors.mucDo)}
                            disabled={isLoading || uploadingVideo}
                            options={[
                                { value: 'Thông thường', label: 'Thông thường' },
                                { value: 'Khẩn cấp', label: 'Khẩn cấp' }
                            ]}
                        />
                        {errors.mucDo && (
                            <p className="mt-1 text-sm text-red-500">{errors.mucDo}</p>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tiêu đề <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="text"
                        name="tieuDe"
                        value={formData.tieuDe}
                        onChange={handleInputChange}
                        placeholder="Nhập tiêu đề (ít nhất 10 ký tự)"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.tieuDe ? 'border-red-500' : 'border-gray-300'}`}
                        disabled={isLoading || uploadingVideo}
                    />
                    {errors.tieuDe && (
                        <p className="mt-1 text-sm text-red-500">{errors.tieuDe}</p>
                    )}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mô tả chi tiết <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        name="moTa"
                        value={formData.moTa}
                        onChange={handleInputChange}
                        placeholder="Mô tả chi tiết nội dung phản ánh (ít nhất 20 ký tự)"
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.moTa ? 'border-red-500' : 'border-gray-300'}`}
                        disabled={isLoading || uploadingVideo}
                    />
                    {errors.moTa && (
                        <p className="mt-1 text-sm text-red-500">{errors.moTa}</p>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Địa điểm <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="viTri"
                            value={formData.viTri}
                            onChange={handleInputChange}
                            placeholder="Nhập địa chỉ/vị trí xảy ra vụ việc"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.viTri ? 'border-red-500' : 'border-gray-300'}`}
                            disabled={isLoading || uploadingVideo}
                        />
                        {errors.viTri && (
                            <p className="mt-1 text-sm text-red-500">{errors.viTri}</p>
                        )}
                    </div>

                    <div className="relative" ref={khuPhoRef}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Khu phố <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="khuPho"
                                value={formData.khuPho}
                                onChange={(e) => {
                                    handleInputChange(e);
                                    setShowKhuPhoDropdown(true);
                                }}
                                onFocus={() => setShowKhuPhoDropdown(true)}
                                placeholder="Nhập hoặc chọn khu phố (1 - 45)"
                                className={`w-full px-3 py-2 pr-8 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.khuPho ? 'border-red-500' : 'border-gray-300'}`}
                                disabled={isLoading || uploadingVideo}
                                autoComplete="off"
                            />
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>

                        {showKhuPhoDropdown && !isLoading && !uploadingVideo && (
                            <div className="absolute z-50 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                                {(() => {
                                    const rawQuery = (formData.khuPho || '').trim().toLowerCase();
                                    const removeAccents = (str) =>
                                        str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D');
                                    const normQuery = removeAccents(rawQuery);

                                    const filtered = KHU_PHO_LIST.filter(item => {
                                        if (!normQuery) return true;
                                        const normItem = removeAccents(item.toLowerCase());
                                        if (normItem.includes(normQuery)) return true;
                                        const kpQuery = normQuery.replace(/^kp\s*/, 'khu pho ');
                                        if (normItem.includes(kpQuery)) return true;
                                        if (/^\d+$/.test(normQuery)) {
                                            const numInItem = item.replace(/\D/g, '');
                                            return numInItem.includes(normQuery);
                                        }
                                        return false;
                                    });

                                    if (filtered.length === 0) {
                                        return (
                                            <div className="px-3 py-2 text-sm text-gray-400 text-center">
                                                Không tìm thấy khu phố phù hợp
                                            </div>
                                        );
                                    }

                                    return filtered.map((item) => (
                                        <div
                                            key={item}
                                            className={`px-3 py-2 text-sm cursor-pointer transition-colors ${formData.khuPho === item ? 'bg-blue-50 font-semibold text-blue-600' : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'}`}
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, khuPho: item }));
                                                if (errors.khuPho) {
                                                    setErrors(prev => {
                                                        const newErrors = { ...prev };
                                                        delete newErrors.khuPho;
                                                        return newErrors;
                                                    });
                                                }
                                                setShowKhuPhoDropdown(false);
                                            }}
                                        >
                                            {item}
                                        </div>
                                    ));
                                })()}
                            </div>
                        )}

                        {errors.khuPho && (
                            <p className="mt-1 text-sm text-red-500">{errors.khuPho}</p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Họ tên người phản ánh <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            name="tenNguoiPhanAnh"
                            value={formData.tenNguoiPhanAnh}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            placeholder="Nhập họ tên người phản ánh"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.tenNguoiPhanAnh ? 'border-red-500' : 'border-gray-300'}`}
                            disabled={isLoading || uploadingVideo}
                        />
                        {errors.tenNguoiPhanAnh && (
                            <p className="mt-1 text-xs text-red-500 font-medium leading-tight">{errors.tenNguoiPhanAnh}</p>
                        )}
                    </div>
                    
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Số điện thoại <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="tel"
                            name="soDienThoaiNguoiPhanAnh"
                            value={formData.soDienThoaiNguoiPhanAnh}
                            onChange={handleInputChange}
                            onBlur={handleBlur}
                            maxLength={10}
                            placeholder="Nhập SĐT người phản ánh (VD: 0901234567)"
                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.soDienThoaiNguoiPhanAnh ? 'border-red-500' : 'border-gray-300'}`}
                            disabled={isLoading || uploadingVideo}
                        />
                        {errors.soDienThoaiNguoiPhanAnh && (
                            <p className="mt-1 text-xs text-red-500 font-medium leading-tight">{errors.soDienThoaiNguoiPhanAnh}</p>
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        File đính kèm (Ảnh/Video) <span className="text-red-500">*</span>
                    </label>
                    
                    <div 
                        className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg transition-colors ${
                            isDragging ? 'border-blue-500 bg-blue-50' : 
                            errors.files ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-blue-400'
                        } ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => !uploadingVideo && fileInputRef.current?.click()}
                    >
                        <div className="space-y-2 text-center">
                            <UploadCloud className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600 justify-center">
                                <span className="relative rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                    <span>Tải file lên</span>
                                    <input 
                                        ref={fileInputRef}
                                        type="file" 
                                        multiple 
                                        accept="image/jpeg, image/png, video/mp4" 
                                        className="sr-only" 
                                        onChange={handleFileChange}
                                        disabled={isLoading || uploadingVideo}
                                    />
                                </span>
                                <p className="pl-1">hoặc kéo thả vào đây</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG tối đa 5MB, MP4 tối đa 50MB (tổng tối đa 5 file)</p>
                        </div>
                    </div>
                    {errors.files && (
                        <p className="mt-1 text-sm text-red-500">{errors.files}</p>
                    )}

                    {/* File List Preview */}
                    {formData.files.length > 0 && (
                        <ul className="mt-4 space-y-2">
                            {formData.files.map((file, index) => {
                                const isVideo = file.type.startsWith('video/');
                                return (
                                    <li key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="flex items-center space-x-3 overflow-hidden">
                                            <div className="flex-shrink-0">
                                                {isVideo ? (
                                                    <div className="p-2 bg-blue-100 rounded-lg">
                                                        <Video className="h-5 w-5 text-blue-600" />
                                                    </div>
                                                ) : (
                                                    <div className="p-2 bg-green-100 rounded-lg">
                                                        <ImageIcon className="h-5 w-5 text-green-600" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {file.name}
                                                </p>
                                                <p className="text-xs text-gray-500">
                                                    {(file.size / (1024 * 1024)).toFixed(2)} MB
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeFile(index);
                                            }}
                                            disabled={uploadingVideo}
                                            className="ml-4 flex-shrink-0 p-1 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                                        >
                                            <X className="h-5 w-5" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </BaseModal>
    );
};

export default ReportCreateModal;
